import React, { useMemo, useState } from 'react';
import { useOptionalWebSocket } from '../../../contexts/WebSocketContext';
import { getCreatureMechanicsData } from '../../../utils/combat/CreatureAbilityCatalog';
import './CreatureEquipmentControls.css';

const CreatureEquipmentControls = ({ token }) => {
  const { currentSessionId, sendBattleRoomMutation } = useOptionalWebSocket() || {};
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const mechanics = useMemo(() => getCreatureMechanicsData(token), [token]);
  const equipmentState = token?.runtimeOverlay?.equipmentState ?? token?.equipmentState ?? mechanics?.equipmentState;
  const shieldBonus = Number(equipmentState?.shieldArmorClassBonus);
  const shieldEquipped = equipmentState?.shieldEquipped === true;
  const armorClass = Number(token?.runtimeOverlay?.ac ?? token?.ac);
  const unlocksTwoHanded = Array.isArray(mechanics?.actions) && mechanics.actions.some((action) => (
    action?.mechanics?.prerequisites?.some((prerequisite) => prerequisite?.kind === 'noShield')
  ));

  if (!mechanics?.contentVersion || !Number.isInteger(shieldBonus) || shieldBonus <= 0) return null;
  const nextArmorClass = Number.isFinite(armorClass) ? armorClass + (shieldEquipped ? -shieldBonus : shieldBonus) : null;

  const changeShield = async () => {
    if (!currentSessionId || typeof sendBattleRoomMutation !== 'function') {
      setError('Join the live BattleRoom before changing this creature’s equipment.');
      return;
    }
    setPending(true);
    setMessage('');
    setError('');
    try {
      const response = await sendBattleRoomMutation('creature:equipment:update', {
        tokenId: token.id,
        shieldEquipped: !shieldEquipped,
      });
      const result = response?.result || {};
      setMessage(`Shield ${result.shieldEquipped ? 'donned' : 'doffed'}. AC ${result.armorClass}.`);
    } catch (cause) {
      setError(cause?.message || 'The shield change could not be saved.');
    } finally {
      setPending(false);
    }
  };

  return (
    <section className="br-creature-equipment" aria-labelledby="br-creature-equipment-title">
      <div className="br-creature-equipment__heading">
        <div>
          <span>Equipment state</span>
          <h4 id="br-creature-equipment-title">Shield</h4>
        </div>
        <strong>{shieldEquipped ? 'Equipped' : 'Doffed'}</strong>
      </div>
      <dl>
        <div><dt>Current AC</dt><dd>{Number.isFinite(armorClass) ? armorClass : 'Missing'}</dd></div>
        <div><dt>After change</dt><dd>{nextArmorClass ?? 'Blocked'}</dd></div>
      </dl>
      <p>Donning or doffing a shield uses this creature’s action during combat.{unlocksTwoHanded && shieldEquipped ? ' Doffing it enables embedded two-handed weapon modes on a later available action.' : ''}</p>
      <button type="button" onClick={changeShield} disabled={pending || nextArmorClass === null}>
        {pending ? 'Saving equipment…' : shieldEquipped ? 'Doff shield' : 'Don shield'}
      </button>
      {message && <p className="br-creature-equipment__status" role="status">{message}</p>}
      {error && <p className="br-creature-equipment__error" role="alert">{error}</p>}
    </section>
  );
};

export default CreatureEquipmentControls;
